import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import smoothscroll from "smoothscroll-polyfill";
import { Button } from "@aqar-app/aqar-design-system";
import classnames from "classnames/bind";
import { useApolloClient, useQuery } from "@apollo/client";
import { useRouter } from "next/router";
import LinkWidget from "./LinkWidget";
import {
  GET_ALL_CATEGORIES,
  getCachedCategory,
} from "../../graphql/queries/categories";
import actions from "../../contexts/listingsList/listingsList.actions";
import { useListingListDispatch } from "../../contexts/listingsList/listingsList.provider";
import useHorizontalScroll from "../../hooks/useHorizontalScroll";
import styles from "./categoriesList.module.scss";
import sharedStyles from "../../styles/shared.module.scss";
import { trackGA4Event } from "../../lib/tracking/gaHelpers";
import { useAppState } from "../../contexts/app/app.provider";
import { events } from "../../lib/tracking/constants";

const { toggleSearchbar } = actions;
const cx = classnames.bind(styles);

const CategoriesList = React.memo(({ activeCategory, mode }) => {
  const dispatch = useListingListDispatch();
  const { isMapPage } = useAppState();
  const apolloClient = useApolloClient();
  const [searchIconColor, setSearchIconColor] = useState("blue");
  const [categoriesPositions, setCategoriesPositions] = useState({});
  const categoriesRef = useRef();
  const categoryRef = useRef({});

  const router = useRouter();
  const { category: prevCategory } = router.query;

  useHorizontalScroll(categoriesRef);

  const getCategoryPosition = (index) => {
    index = index - 1 <= 0 ? 0 : index - 1;
    const categoriesPositionsValues = Object.values(categoriesPositions);
    if (index === 0) {
      return categoriesPositionsValues
        .slice(0, categoriesPositionsValues.length - 1)
        .reduce((a, b) => a + b, 0);
    }
    if (categoriesPositionsValues.length > 0) {
      return categoriesPositionsValues
        .slice(0, index)
        .reduce((a, b) => a - b, 0);
    }
  };

  const pushListingCategoryChangeEvent = (newCategory) => {
    const event = {
      event: events.CHANGE_LISTING_CATEGORY,
      listing_category_name: newCategory.uri,
      listing_category: newCategory.id,
      listing_previous_category: prevCategory || "",
    };
    trackGA4Event(event);
  };

  const pushShowListingsFiltersEvent = () => {
    const event = {
      event: events.OPEN_LISTING_LIST_FILTERS,
      listing_view: isMapPage ? "map" : "list",
      listing_category: getCachedCategory(apolloClient, prevCategory)?.id || 0,
    };

    trackGA4Event(event);
  };

  useEffect(() => {
    smoothscroll.polyfill();
    Object.entries(categoryRef.current).forEach(([catUri, el]) => {
      if (!el) return;
      setCategoriesPositions((prev) => {
        return {
          ...prev,
          ...{ [catUri]: el.getBoundingClientRect().width },
        };
      });
    });
  }, []);

  useEffect(() => {
    const activeCategoryIndex = Object.keys(categoriesPositions).findIndex(
      (uri) => uri === activeCategory
    );
    categoriesRef?.current?.scrollTo({
      left: getCategoryPosition(activeCategoryIndex),
      behavior: "smooth",
    });
  }, [activeCategory, categoriesPositions, categoriesRef]);

  const { data, loading, error } = useQuery(GET_ALL_CATEGORIES, {
    variables: { lang: "ar" },
  });

  if (error) {
    return null;
  }

  if (loading) {
    return null; // TODO categories skeleton (if necessary) or null
  }

  const containerClasses = cx("categoriesContainer", {
    absolute: mode === "map",
  });

  const categoriesListClasses = classnames({
    [styles.categoriesList]: true,
    [sharedStyles.touchActionPanX]: mode === "map",
  });

  return (
    <div className={containerClasses}>
      <div className={styles.searchWrapper}>
        <Button
          suffixIcon="search"
          iconColor={searchIconColor}
          label="بحث"
          size="x-small"
          className={cx("linkWidget", { search: true })}
          onMouseEnter={() => setSearchIconColor("white")}
          onMouseLeave={() => setSearchIconColor("blue")}
          onClick={() => {
            // eslint-disable-next-line no-restricted-globals
            history.pushState({}, "");
            dispatch(toggleSearchbar(true));
            pushShowListingsFiltersEvent();
          }}
        />
      </div>
      <ul
        className={categoriesListClasses}
        data-cy="categories-list"
        ref={categoriesRef}
      >
        {data.Web.categories.map((category, index) => {
          return (
            <li
              key={category.uri}
              id={activeCategory === category.uri ? "selected-category" : ""}
              ref={(el) => {
                categoryRef.current[category.uri] = el;
              }}
            >
              <div
                onClick={() => pushListingCategoryChangeEvent(category)}
                onKeyPress={() => pushListingCategoryChangeEvent(category)}
                role="button"
                tabIndex={index}
              >
                <LinkWidget
                  title={
                    category.name === "غير محدد"
                      ? "الكل"
                      : category.plural || category.name
                  }
                  link={category.uri}
                  selected={activeCategory === category.uri}
                  path={mode === "map" ? "/map" : ""}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

CategoriesList.propTypes = {
  activeCategory: PropTypes.string,
  mode: PropTypes.oneOf(["map", "listing"]),
};

CategoriesList.defaultProps = {
  activeCategory: "عقارات",
  mode: "listing",
};

export default CategoriesList;
